import { USStateNames, type USStateAbbreviation } from '@/utils/constants';
import { getSlug } from '@/utils/helpers';

export const SHOP_PLANS_BASE_PATH = '/shop-individual-health-insurance-plans';

const STATE_SEGMENT_OVERRIDES: Partial<Record<USStateAbbreviation, string>> = {
    DC: 'district-of-columbia',
};

const STATE_DISPLAY_NAME_OVERRIDES: Partial<Record<USStateAbbreviation, string>> = {
    DC: 'District of Columbia',
};

const COUNTY_SLUG_OVERRIDES: Record<string, string> = {
    'AK|aleutians-west-census-area': 'aleutians-west',
    'AK|bethel-census-area': 'bethel',
    'AK|dillingham-census-area': 'dillingham',
    'AK|hoonah-angoon-census-area': 'hoonah-angoon',
    'AK|kusilvak-census-area': 'kusilvak',
    'AK|nome-census-area': 'nome',
    'AK|petersburg-census-area': 'petersburg',
    'AK|prince-of-wales-hyder-census-area': 'prince-of-wales-hyder',
    'AK|southeast-fairbanks-census-area': 'southeast-fairbanks',
    'AK|yukon-koyukuk-census-area': 'yukon-koyukuk',
};

const COUNTY_SUFFIX_ALIASES = [
    'county',
    'parish',
    'borough',
    'census-area',
    'city-and-borough',
    'municipality',
    'city',
] as const;

type ShopPlansPathKind = 'base' | 'zip' | 'state-root' | 'state' | 'state-county' | 'other';

export type ParsedShopPlansPath = {
    kind: ShopPlansPathKind;
    stateCode?: string;
    stateSegment?: string;
    countySlug?: string;
    zipCode?: string;
};

function normalizeStateCode(value: string): string {
    return String(value || '').trim().toUpperCase();
}

function isKnownStateCode(value: string): value is USStateAbbreviation {
    return value in USStateNames;
}

function normalizePathname(pathname: string): string {
    const withLeadingSlash = pathname.startsWith('/') ? pathname : `/${pathname}`;
    const trimmed = withLeadingSlash.replace(/\/+$/, '');
    return trimmed || '/';
}

export function getStateDisplayNameFromCode(stateCode: string): string {
    const normalizedCode = normalizeStateCode(stateCode);
    if (!isKnownStateCode(normalizedCode)) {
        return normalizedCode;
    }

    return STATE_DISPLAY_NAME_OVERRIDES[normalizedCode] ?? USStateNames[normalizedCode];
}

export function getStateSegmentFromCode(stateCode: string): string {
    const normalizedCode = normalizeStateCode(stateCode);
    if (!normalizedCode) {
        return '';
    }

    const stateName = getStateDisplayNameFromCode(normalizedCode);
    const stateSlug = STATE_SEGMENT_OVERRIDES[normalizedCode as USStateAbbreviation] ?? getSlug(stateName);

    if (!stateSlug) {
        return normalizedCode;
    }

    return `${normalizedCode}-${stateSlug}`;
}

export function parseStateSegment(stateSegment: string): { stateCode: string; stateSegment: string } | null {
    const rawSegment = decodeURIComponent(String(stateSegment || '').trim());
    if (!rawSegment) {
        return null;
    }

    const stateMatch = rawSegment.match(/^([A-Za-z]{2})(?:-(.+))?$/);
    if (!stateMatch) {
        return null;
    }

    const stateCode = normalizeStateCode(stateMatch[1] || '');
    if (!isKnownStateCode(stateCode)) {
        return null;
    }

    return {
        stateCode,
        stateSegment: rawSegment,
    };
}

export function normalizeCountySlug(rawCountySlug: string): string {
    return getSlug(decodeURIComponent(String(rawCountySlug || '').trim()));
}

export function getCountySlugCandidatesFromName(countyName: string, stateCode?: string): string[] {
    const normalizedStateCode = normalizeStateCode(stateCode || '');
    const baseSlug = normalizeCountySlug(countyName);
    if (!baseSlug) {
        return [];
    }

    const override = COUNTY_SLUG_OVERRIDES[`${normalizedStateCode}|${baseSlug}`];
    const candidates = new Set<string>();

    if (override) {
        candidates.add(override);
    }

    candidates.add(baseSlug);

    for (const suffix of COUNTY_SUFFIX_ALIASES) {
        const suffixToken = `-${suffix}`;
        if (baseSlug.endsWith(suffixToken)) {
            const withoutSuffix = baseSlug.slice(0, -suffixToken.length);
            if (withoutSuffix) {
                candidates.add(withoutSuffix);
            }
        }
    }

    return Array.from(candidates);
}

export function getCountySlugFromName(countyName: string, stateCode?: string): string {
    const candidates = getCountySlugCandidatesFromName(countyName, stateCode);
    return candidates[0] ?? '';
}

export function buildShopPlansStateRootPath(): string {
    return `${SHOP_PLANS_BASE_PATH}/state`;
}

export function buildShopPlansStatePath(stateCode: string): string {
    const stateSegment = getStateSegmentFromCode(stateCode);
    if (!stateSegment) {
        return buildShopPlansStateRootPath();
    }
    return `${SHOP_PLANS_BASE_PATH}/state/${encodeURIComponent(stateSegment)}`;
}

export function buildShopPlansCountyPath(stateCode: string, countySlug: string): string {
    const stateSegment = getStateSegmentFromCode(stateCode);
    const normalizedCountySlug = normalizeCountySlug(countySlug);

    if (!stateSegment) {
        return buildShopPlansStateRootPath();
    }

    if (!normalizedCountySlug) {
        return `${SHOP_PLANS_BASE_PATH}/state/${encodeURIComponent(stateSegment)}`;
    }

    return `${SHOP_PLANS_BASE_PATH}/state/${encodeURIComponent(stateSegment)}/county/${encodeURIComponent(normalizedCountySlug)}`;
}

export function parseShopPlansPath(pathname: string): ParsedShopPlansPath {
    const normalizedPath = normalizePathname(pathname);

    if (normalizedPath === SHOP_PLANS_BASE_PATH) {
        return { kind: 'base' };
    }

    if (normalizedPath === `${SHOP_PLANS_BASE_PATH}/state`) {
        return { kind: 'state-root' };
    }

    const zipMatch = normalizedPath.match(new RegExp(`^${SHOP_PLANS_BASE_PATH}/zip/([^/]+)$`));
    if (zipMatch?.[1]) {
        return {
            kind: 'zip',
            zipCode: decodeURIComponent(zipMatch[1]),
        };
    }

    const stateCountyMatch = normalizedPath.match(
        new RegExp(`^${SHOP_PLANS_BASE_PATH}/state/([^/]+)/county/([^/]+)$`)
    );

    if (stateCountyMatch?.[1]) {
        const parsedState = parseStateSegment(stateCountyMatch[1]);
        if (!parsedState) {
            return { kind: 'other' };
        }

        return {
            kind: 'state-county',
            stateCode: parsedState.stateCode,
            stateSegment: parsedState.stateSegment,
            countySlug: normalizeCountySlug(stateCountyMatch[2] || ''),
        };
    }

    const stateMatch = normalizedPath.match(new RegExp(`^${SHOP_PLANS_BASE_PATH}/state/([^/]+)$`));
    if (stateMatch?.[1]) {
        const parsedState = parseStateSegment(stateMatch[1]);
        if (!parsedState) {
            return { kind: 'other' };
        }

        return {
            kind: 'state',
            stateCode: parsedState.stateCode,
            stateSegment: parsedState.stateSegment,
        };
    }

    return { kind: 'other' };
}

export function titleCaseFromSlug(slug: string): string {
    const normalizedSlug = normalizeCountySlug(slug);
    if (!normalizedSlug) {
        return '';
    }

    return normalizedSlug
        .split('-')
        .filter(Boolean)
        .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
        .join(' ');
}
